import { useNavigate, useSearchParams } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import HeroSection from "../../components/UserComponents/heroSection.jsx";
import ExperienceCard from "../../components/vendors/ExperienceCard";
import {
  useDurations,
  usePublicCategories,
  usePublicExperiences,
  usePublicLocations,
} from "../../custom-hooks/queries";
import { debounce } from "../../utils/HelperMethod.js";
import CustomFilterDropdown from "../../components/common/CustomFilterDropdown.jsx";
import ExperiencesWithMap from "../../components/UserComponents/ExpMap.jsx";
import { FiList, FiMap } from "react-icons/fi";
import { useAuthStore } from "../../store/useAuthStore.js";
import { jwtDecode } from "jwt-decode";

/* ---------------- Skeletons ---------------- */
const ExperienceCardSkeleton = () => (
  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
    <div className="h-48 bg-gray-200" />
    <div className="p-4 space-y-3">
      <div className="h-3 w-20 bg-gray-200 rounded" />
      <div className="h-4 w-full bg-gray-200 rounded" />
      <div className="h-4 w-3/4 bg-gray-200 rounded" />
    </div>
  </div>
);

const MapSkeleton = () => (
  <div className="w-full h-full rounded-xl bg-gray-200 animate-pulse" />
);

const ExperienceCardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [decoded, setDecoded] = useState("");

  const [mapSelectedExperienceId, setMapSelectedExperienceId] = useState(null);
  const [mobileSelectedExperience, setMobileSelectedExperience] =
    useState(null);

  const token = useAuthStore.getState().token;

  useEffect(() => {
    if (token) {
      const tokenDecoded = jwtDecode(token);
      setDecoded(tokenDecoded);
    }
  }, []);

  /* Mobile: list (cards) default; map via toggle */
  const [mobileView, setMobileView] = useState("cards"); // "map" | "cards"

  const handleSearchChange = (value) => {
    setSearch(value); // input ke liye instant
    debouncedSearch(value); // API ke liye delayed
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedValue(value); // API trigger value
      }, 500),
    [],
  );

  const isTop = searchParams.get("isTop") === "true";
  // const isMobile = window.innerWidth < 1024;

  const { data: durationsData } = useDurations();
  const { data: categoriesData } = usePublicCategories();
  const { data: locationsData } = usePublicLocations();

  const categoriesFilters =
    categoriesData?.data?.map((d) => ({ id: d.id, name: d.name })) || [];

  const locationsFilters =
    locationsData?.data?.map((d) => ({ label: d.label })) || [];

  const durationOptions =
    durationsData?.data?.map((d) => ({ id: d.value, name: d.value })) || [];

  const sortOptions = [
    { id: "priceLowToHigh", name: "Price: Low to High" },
    { id: "priceHighToLow", name: "Price: High to Low" },
  ];

  const filterOptions = {
    categories: categoriesFilters,
    locations: locationsFilters,
    durations: durationOptions,
    sort: sortOptions,
  };
  const [city, country] =
    selectedLocation?.split(",").map((v) => v.trim()) || [];

  useEffect(() => {
    // agar koi map pin select nahi rehni chahiye
    // aur data full aa gaya hai, to reset kar do
    if (
      !selectedCategory &&
      !selectedLocation &&
      !selectedDuration &&
      !sortBy
    ) {
      setMapSelectedExperienceId(null);
    }
  }, [selectedCategory, selectedLocation, selectedDuration, sortBy]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      // desktop pe aate hi mobile-only states reset
      if (!mobile) {
        setMobileView("cards");
        setMobileSelectedExperience(null);
      }
    };

    window.addEventListener("resize", handleResize);

    // initial run (important)
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const { data: allExperiencesData, isLoading } = usePublicExperiences({
    page: 1,
    pageSize: 50,
    isTop,
    search: debouncedValue,
    categoryId: selectedCategory,
    city,
    country,
    duration: selectedDuration,
    priceLowToHigh: sortBy === "priceLowToHigh",
    priceHighToLow: sortBy === "priceHighToLow",
    userId: decoded?.userId,
  });

  const experiences =
    allExperiencesData?.data?.data ||
    allExperiencesData?.items ||
    allExperiencesData?.data ||
    [];

  const filteredExperiences = useMemo(() => {
    if (!mapSelectedExperienceId) return experiences;
    return experiences.filter(
      (exp) => exp.experienceId === mapSelectedExperienceId,
    );
  }, [experiences, mapSelectedExperienceId]);

  const showMap = isMobile || Boolean(selectedLocation);

  /* Reset map selection on filter change (existing behavior) */
  useEffect(() => {
    if (isMobile) {
      setMobileView("cards");
      setMapSelectedExperienceId(null);
    }
  }, [selectedLocation, selectedCategory, selectedDuration, sortBy]);

  const handleExperienceClick = (id) => {
    navigate(`/user/experience/${id}`);
  };

  const handlePinClick = (experienceId) => {
    const selected = experiences.find((e) => e.experienceId === experienceId);

    if (isMobile) {
      setMobileSelectedExperience(selected);
    } else {
      setMapSelectedExperienceId(experienceId);
    }
  };

  return (
    <>
      {/* HERO */}
      <HeroSection
        searchValue={search}
        onSearchChange={handleSearchChange}
        title="Curated Experiences. Clear availability. Easy Booking"
        description="Everything you need to find and book the right experience - without the friction"
        searchPlaceholder="Search experiences or locations"
        cateogry={setSelectedCategory}
        location={setSelectedLocation}
        duration={setSelectedDuration}
        sorting={setSortBy}
        filterOptions={filterOptions}
        expDetails={true}
      />

      {/* MOBILE TOGGLE (already existing) */}
      {/* 🔹 MOBILE ICON TOGGLE (always visible on small screens) */}
      {isMobile && (
        <div className="px-4 mt-3 flex justify-center">
          <div className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1 shadow-sm">
            <button
              onClick={() => setMobileView("map")}
              className={`p-2 rounded-full transition-all ${
                mobileView === "map"
                  ? "bg-toucanTeal text-white shadow"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
              aria-label="Map View"
            >
              <FiMap size={16} />
            </button>

            <button
              onClick={() => setMobileView("cards")}
              className={`p-2 rounded-full transition-all ${
                mobileView === "cards"
                  ? "bg-toucanTeal text-white shadow"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
              aria-label="List View"
            >
              <FiList size={16} />
            </button>
          </div>
        </div>
      )}
      {/* 🔹 MOBILE ICON TOGGLE (always visible on small screens) */}

      {/* CONTENT */}
      <div className="container mx-auto px-4 py-12">
        {/* NO MAP */}
        {!showMap && (
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <ExperienceCardSkeleton key={i} />
                ))
              : filteredExperiences.map((item) => (
                  <div
                    key={item.experienceId}
                    onClick={() => handleExperienceClick(item.experienceId)}
                    className="cursor-pointer"
                  >
                    <ExperienceCard
                      id={item.experienceId}
                      img={item.imageUrl}
                      title={item.title}
                      location={item.location}
                      price={item.priceFrom}
                      time={item.duration}
                      rating={item.ratingAvg}
                      tag="Experience"
                    />
                  </div>
                ))}
          </div>
        )}

        {/* MAP MODE */}
        {showMap && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* CARDS */}
            {(!isMobile || mobileView === "cards") && (
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2">
                {isLoading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <ExperienceCardSkeleton key={i} />
                    ))
                  : filteredExperiences.map((item) => (
                      <div
                        key={item.experienceId}
                        onClick={() => handleExperienceClick(item.experienceId)}
                        className="cursor-pointer"
                      >
                        <ExperienceCard
                          id={item.experienceId}
                          img={item.imageUrl}
                          title={item.title}
                          location={item.location}
                          price={item.priceFrom}
                          time={item.duration}
                          rating={item.ratingAvg}
                          tag="Experience"
                        />
                      </div>
                    ))}
              </div>
            )}

            {/* MAP */}
            {(!isMobile || mobileView === "map") && (
              <div className="h-[75vh] sticky top-24">
                {isLoading ? (
                  <MapSkeleton />
                ) : (
                  <ExperiencesWithMap
                    experiences={experiences}
                    onPinClick={handlePinClick}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* MOBILE BOTTOM MODAL */}
      {mobileSelectedExperience && (
        <div className="fixed inset-0 z-50 flex items-end lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileSelectedExperience(null)}
          />
          <div className="relative w-full rounded-t-2xl p-4 max-h-[70vh] overflow-y-auto bg-white">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-3" />
            <span
              onClick={() =>
                handleExperienceClick(mobileSelectedExperience.experienceId)
              }
            >
              <ExperienceCard
                id={mobileSelectedExperience.experienceId}
                img={mobileSelectedExperience.imageUrl}
                title={mobileSelectedExperience.title}
                location={mobileSelectedExperience.location}
                price={mobileSelectedExperience.priceFrom}
                time={mobileSelectedExperience.duration}
                rating={mobileSelectedExperience.ratingAvg}
                tag="Experience"
                mapview
              />
            </span>
            <button
              onClick={() => setMobileSelectedExperience(null)}
              className="w-full mt-4 py-3 border-t text-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ExperienceCardPage;
